import {Injectable} from '@angular/core';
import * as Moment from 'moment';
import {RecentRequisitionViewModel} from '../../../shared/models/ocl';
import * as Sentry from '@sentry/angular-ivy';

@Injectable()
export class RecentRequisitionService {

  constructor() {
    this.verifyRecentRequisitions(this.getRecentRequisitions());
  }

  public static isLocalStorageAvailable(): boolean {
    if (typeof localStorage === 'undefined') {
      console.log('This browser doesn\'t support LocalStorage');
      return false;
    }
    return true;
  }

  public static isWebWorkersAvailable(): boolean {
    if (typeof Worker === 'undefined') {
      console.log('This browser doesn\'t support WebWorkers');
      return false;
    }
    return true;
  }

  public getRecentRequisitions(): RecentRequisitionViewModel[] {
    if (!RecentRequisitionService.isLocalStorageAvailable()) {
      return [];
    }
    const recentRequisitions = localStorage.getItem('recentRequisitions');
    let result: RecentRequisitionViewModel[];
    if (recentRequisitions) {
      result = JSON.parse(recentRequisitions);
    } else {
      result = [];
    }

    return result;
  }

  public verifyRecentRequisitions(requisitions: any) {
    const recent = requisitions;
    const requisitionsIds = recent
      .filter((requisition: any) => {
        const deleteDate = Moment(requisition.shouldBeDeletedDate);
        const currentDate = Moment();
        return deleteDate.isBefore(currentDate);
      })
      .map((p: any) => p.id);

    if (requisitionsIds.length > 0) {
      try {
        requisitionsIds.forEach((id: any) =>
          recent.splice(
            recent.findIndex((p: any) => p.id === id),
            1,
          ),
        );
      } catch (e) {
        Sentry.captureException(e);
      }
      this.save(requisitions);
    }
  }

  public updateRecentRecentRequisitions(requisition: RecentRequisitionViewModel): void {
    if (!RecentRequisitionService.isLocalStorageAvailable()) {
      return;
    }
    if (requisition === undefined || requisition === null) {
      return;
    }
    const requisitions = this.getRecentRequisitions();
    const index = requisitions.findIndex((r) => requisition.id === r.id);
    if (index === -1) {
      return;
    }

    requisitions.splice(index, 1);
    requisition.recentDate = Moment().toDate();
    requisition.shouldBeDeletedDate = Moment().add('12', 'hours').toDate();
    requisitions.push(requisition);
    this.save(requisitions);
  }

  public addRecentRecentRequisitions(requisition: RecentRequisitionViewModel): void {
    if (!RecentRequisitionService.isLocalStorageAvailable()) {
      return;
    }
    if (requisition === undefined || requisition === null) {
      return;
    }
    if (this.exists(requisition.id as number)) {
      return;
    }

    const requisitions = this.getRecentRequisitions();
    requisition.recentDate = Moment().toDate();
    requisition.shouldBeDeletedDate = Moment().add('12', 'hours').toDate();
    requisitions.push(requisition);
    this.save(requisitions);
  }

  private save(requisitions: any): void {
    const reqString = JSON.stringify(requisitions);
    localStorage.setItem('recentRequisitions', reqString);
  }

  public async hasAny(): Promise<boolean> {
    if (!RecentRequisitionService.isLocalStorageAvailable()) {
      return new Promise<boolean>(() => false);
    }
    return this.getRecentRequisitions().length > 0;
  }

  public exists(requisitionId: number): boolean {
    if (!RecentRequisitionService.isLocalStorageAvailable()) {
      return false;
    }
    const requisitions = this.getRecentRequisitions();
    return requisitions.findIndex((requisition: RecentRequisitionViewModel) => requisition.id === requisitionId) !== -1;
  }
}
